import { isLegendItemType } from './Positionable/legendItemType';

/**
 * SW2-2541 — locking previous site map versions.
 *
 * SW2-2346 made a previous version append-only: legend items can be added and removed there, but
 * not edited. With the tenant feature on AND either the tenant Admin's "Lock Across All Clients"
 * or the client's `lock_previous_map_versions` on, adds and removes of legend items and notes are
 * refused on a previous version too, except for the exempt roles. Notes were added after the first
 * release: they are versioned the same way as legend items, and the lock let them through.
 *
 * The server enforces this (`mapVersionLockService` in the backend). This copy only decides what
 * the editor offers, so a user is not handed a tool that will be refused on save. Keep the two
 * role lists the same.
 */

export const LOCK_PREVIOUS_SITE_MAP_VERSIONS = 'Lock Previous Site Map Versions';

export const MAP_VERSION_LOCK_EXEMPT_ROLES: readonly string[] = [
  'Super Admin',
  'Admin',
  'Divisions Admin',
  'Regional Manager',
];

export const MAP_VERSION_LOCKED_NOTICE =
  'This is a previous version of the map. Legend items and notes cannot be added or removed here.';

/** Tooltip on a disabled "Remove From Map", worded like the existing Edit one (SW2-2346). */
export const MAP_VERSION_REMOVE_LOCKED_MESSAGE =
  'Legend item cannot be removed from previous versions of map!';
export const MAP_VERSION_NOTE_REMOVE_LOCKED_MESSAGE = 'Note cannot be removed from previous versions of map!';

/**
 * The positionable types the lock covers: legend items (any spelling, see legendItemType.js) and
 * notes (`commentables`). Findings and assets are not locked. Keep in step with the backend's
 * `MAP_VERSION_LOCKED_TYPES`.
 */
export const isMapVersionLockedType = (type: string | null | undefined): boolean =>
  isLegendItemType(type) || type === 'commentables';

/** The disabled "Remove From Map" tooltip for a locked feature of this type. */
export const mapVersionRemoveLockedMessage = (type: string | null | undefined): string =>
  type === 'commentables' ? MAP_VERSION_NOTE_REMOVE_LOCKED_MESSAGE : MAP_VERSION_REMOVE_LOCKED_MESSAGE;

/** The right-click "Add …" options left on a version: none of a locked type while it's locked. */
export const withoutLockedAdds = <T extends { positionableType?: string }>(options: T[], locked: boolean): T[] =>
  locked ? options.filter((option) => !isMapVersionLockedType(option.positionableType)) : options;

/** Tooltip on the legend card's locked Added On / Removed On dates. */
export const MAP_VERSION_DATE_LOCKED_MESSAGE =
  "Legend item dates are locked because this map's previous versions are locked.";

type LockInput = {
  tenantHasFeature: (feature: string) => boolean;
  client: { lock_previous_map_versions?: boolean } | null | undefined;
  /** The tenant Admin's "Lock Across All Clients" — overrides the client switch. */
  tenantLocksAllClients?: boolean;
  roleName: string | null | undefined;
};

/**
 * Whether this map's history is locked for this user, on any version. Gates the legend card's
 * Added On / Removed On date pickers: moving either date moves an item into or out of earlier
 * versions, so they are locked on the latest version too.
 */
export const isMapHistoryLockedForUser = ({
  tenantHasFeature,
  client,
  tenantLocksAllClients,
  roleName,
}: LockInput): boolean =>
  (!!tenantLocksAllClients || !!client?.lock_previous_map_versions) &&
  tenantHasFeature(LOCK_PREVIOUS_SITE_MAP_VERSIONS) &&
  !(roleName && MAP_VERSION_LOCK_EXEMPT_ROLES.includes(roleName));

/** Whether the open version is locked for this user's legend item and note adds and removes. */
export const isLegendLockedOnVersion = ({
  isLatestMapVersion,
  ...rest
}: LockInput & {
  /** `undefined` means no version chosen yet, which must not lock anything. */
  isLatestMapVersion: boolean | undefined;
}): boolean => isLatestMapVersion === false && isMapHistoryLockedForUser(rest);
